<template>
  <el-form v-if="report.id" class="report-card el-card box-card is-always-shadow update-card">
    <div class="header-item">
      <el-form-item>
        <h4 class="title">{{ $t('base.updates') }}:</h4>
      </el-form-item>
    </div>
    <div class="updates">
      <el-button
        v-if="updateIndex == -1"
        @click="addNewUpdate()"
        class="add-update-button"
        type="primary"
        icon="el-icon-plus"
        circle></el-button>
      <el-form-item v-for="(update,index) in updatesList"
                    :key="update.id"
                    class="update"
                    :error="index === updateIndex ? currentUpdateError : null">
        <div v-if="index !== updateIndex"
             class="restriction-cover"
             v-bind:class="{'no-interaction' : isUpdateInCreation}"
             @click="selectUpdate(index, update)"></div>
        <el-input
          :id="'textarea-'+index"
          type="textarea"
          :rows="6"
          v-model="update.content"
          @blur="validateUpdate(update)"
        ></el-input>
        <div v-if="index === updateIndex" class="update-controls">
          <el-button
            v-if="update.id || index === updatesList.length - 1"
            @click="patchUpdateOrReport(update)"
            size="mini"
            type="success"
            v-bind:loading="saveInProgress"
            icon="el-icon-check">
            {{ $t('new_report.send_report') }}
          </el-button>
          <el-button
            v-if="!update.id && index !== updatesList.length - 1"
            @click="createUpdate(update)"
            size="mini"
            type="success"
            v-bind:loading="saveInProgress"
            icon="el-icon-check">
            {{ $t('new_report.send_report') }}
          </el-button>
          <el-button
            @click="closeUpdate(update)"
            size="mini"
            icon="el-icon-close">
            {{ $t('base.cancel') }}
          </el-button>
        </div>
        <div v-if="!(isUpdateInCreation && index == 0)" class="edition-information-wrapper">
          <div class="edition-information" v-bind:class="{editionMobil:isMobil}">
            <div>
              <label>
                <strong>
                  {{ $t('new_report.report_created') }}:
                </strong>
                <span>{{ update.createdDate || update.createdAt | moment('dddd, Do MMMM YYYY | HH:mm') }}
                {{ $t('base.clock') }}</span>
              </label>
              <br>
              <label>
                <strong>
                  {{ $t('new_report.report_last_updated') }}:
                </strong>
                <!-- Check for index, if last index use special values (the last index is the report)-->
                <span>
                <template v-if="index < updatesList.length - 1">
                  {{ update.updatedDate || update.updatedAt | moment('dddd, Do MMMM YYYY | HH:mm') }}
                </template>
                <template v-else>
                  <!-- Check for legacy data (no textUpdateDate) -->
                  <template v-if="report.textUpdatedDate">
                    {{ report.textUpdatedDate | moment('dddd, Do MMMM YYYY | HH:mm') }}
                  </template>
                  <template v-else>
                    {{ report.updatedAt | moment('dddd, Do MMMM YYYY | HH:mm') }}
                  </template>
                </template>
                {{ $t('base.clock') }}</span>
              </label>
            </div>
            <!-- Check for index, if last index use special values (the last index is the report)-->
            <div v-if="index < updatesList.length - 1">
              <label>
                <b>{{ $t('current_reports.report_creator') }}: </b>
                <span> {{ update.createdByName }} </span>
                <span v-if="update.createdByAccount == null">({{ $t('settings.account.inactive') }})</span>
                <br>
                <template v-if="update.updatedBy !== update.createdBy">
                  <b>{{ $t('current_reports.report_updater') }}: </b>
                  <span>{{ update.updatedByName }}</span>
                  <span v-if="update.updatedByAccount == null">({{ $t('settings.account.inactive') }})</span>
                </template>
              </label>
            </div>
            <div v-else>
              <label>
                <b>{{ $t('current_reports.report_creator') }}: </b>
                <span>{{ report.createdByName }}</span>
                <span v-if="report.createdByAccount == null">({{ $t('settings.account.inactive') }})</span>
                <br>
                <!-- Check for legacy data (no textUpdatedBy) -->
                <template v-if="report.textUpdatedBy !== report.createdBy ">
                  <b>{{ $t('current_reports.report_updater') }}: </b>
                  <span>{{ report.textUpdatedByName }}</span>
                  <span v-if="report.textUpdatedByAccount == null">({{ $t('settings.account.inactive') }})</span>
                </template>
              </label>
            </div>
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="bottom"></div>
  </el-form>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ReportUpdate from "../../model/ReportUpdate";

const mspStateMappers = createNamespacedHelpers('msp');
const applicationStateMappers = createNamespacedHelpers('application')

export default {
  name: 'EditReportUpdates',
  props: ['report'],
  data() {
    return {
      saveInProgress: false,
      isUpdateInCreation: false,
      updateTextBackUp: undefined,
      updateIndex: -1,
      currentUpdateError: undefined,
    }
  },
  computed: {
    updatesList() {
      return [...this.report.updates, this.report]
    },
    ...applicationStateMappers.mapGetters({isMobil: 'isMobil'}),
  },
  methods: {
    ...mspStateMappers.mapActions(['createReportUpdateAction', 'patchReportUpdateAction']),
    addNewUpdate() {
      this.currentUpdateError = null;
      this.updateIndex = 0;
      this.isUpdateInCreation = true;
      this.report.updates = [new ReportUpdate()].concat(this.report.updates);
    },
    selectUpdate(index, update) {
      // auto focus available text field
      requestAnimationFrame(() => document.getElementById('textarea-' + index).focus());

      if (!this.isUpdateInCreation) {
        if (this.updateIndex !== -1) {
          this.closeUpdate(this.updatesList[this.updateIndex]);
        }
        this.updateIndex = index;
        this.updateTextBackUp = update.content;
      }
    },
    resetUpdateListHandling() {
      this.updateIndex = -1;
      this.isUpdateInCreation = false;
    },
    validateUpdate(update) {
      if (update.content) {
        this.currentUpdateError = null;
      } else {
        this.currentUpdateError = this.$t('new_report.update_required');
      }
    },
    patchUpdateOrReport(update) {
      this.validateUpdate(update);

      // check if report entry needs to be updated. Last entry is always the report entry
      if (!this.currentUpdateError && this.updateIndex === this.updatesList.length - 1) {
        this.$emit('report-content-update');
        return;
      }

      if (!this.currentUpdateError) {
        this.saveInProgress = true;

        update.text = update.content; // TODO: Remove the content from sending to the API !!

        this.patchReportUpdateAction({reportId: this.report.id, update})
          .then((patchedReportUpdate) => {

            // Old entry
            const index = this.report.updates.findIndex(update => update.id === patchedReportUpdate.id);
            this.report.updates[index] = patchedReportUpdate;
            this.report.updates = this.report.updates.slice();

            this.saveInProgress = false;
            this.resetUpdateListHandling();
            this.$notify.success({
              title: this.$t('notification.report_update_patched_successfull'),
              message: this.$t('notification.report_update_patched_successfull_text'),
            });
          }).catch(() => {
          this.saveInProgress = false;
        })
      }
    },
    createUpdate(update) {
      this.validateUpdate(update);
      if (!this.currentUpdateError) {
        this.saveInProgress = true;
        this.createReportUpdateAction({reportId: this.report.id, update})
          .then((newReport) => {
            this.report.updates = newReport.updates;
            this.saveInProgress = false;
            this.resetUpdateListHandling();
            this.$notify.success({
              title: this.$t('notification.report_update_created_successfull'),
              message: this.$t('notification.report_update_created_successfull_text'),
            });
          });
      }
    },
    closeUpdate(update) {
      if (this.isUpdateInCreation) {
        this.report.updates.splice(0, 1);
      } else {
        update.content = this.updateTextBackUp;
      }
      this.currentUpdateError = null;
      this.resetUpdateListHandling();
    },
  }
}
</script>

<style lang="scss" scoped>
.update-card {
  .header-item {
    border-bottom: 1px solid #ebeef5;
    padding: 1rem 1rem 0 1rem;

    .title {
      margin: 0;
    }
  }

  .updates {
    position: relative;
    padding: 1rem 0 0 0;

    .add-update-button {
      position: absolute;
      top: -20px;
      left: calc(50% - 20px);
    }

    .update {
      position: relative;
      padding: 1.5rem 1rem 1.5rem 1rem;
      margin-bottom: 0;
      border-bottom: 1px solid #ebeef5;

      .update-controls {
        position: absolute;
        bottom: -45px;
        left: calc(50% - 107px); // 107px is the half of the update-controls width
        z-index: 10;
      }

      .edition-information-wrapper {
        padding: 0 15px;
        line-height: normal;
      }

      .editionMobil {
        flex-flow: column;
        align-items: flex-start;

        span {
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
